import * as React from "react";
import { useContext, useId } from "react";
import { Field, RadioGroup, Tabs } from "@/components/ui";
import { cn } from "@/lib/utils";
import { PlayerCardReviewDisplayType } from "@/services/bedrock";
import { PlayerCardSettingsContext } from "../Contexts";
import { REVIEW_DISPLAY_TYPE_GROUPS } from "../utils";

interface ReviewDisplayTypePickerProps {
  value: PlayerCardReviewDisplayType;
  onChange: (value: PlayerCardReviewDisplayType) => void;
  excludedTypes?: PlayerCardReviewDisplayType[];
}

const ReviewDisplayTypePicker: React.FC<ReviewDisplayTypePickerProps> = ({ value, onChange, excludedTypes = [] }) => {
  const { User } = useContext(PlayerCardSettingsContext);
  const idPrefix = useId();
  const disabled = !User.hasWritePermission;
  const groups = REVIEW_DISPLAY_TYPE_GROUPS.map(group => ({
    ...group,
    options: group.options.filter(option => !excludedTypes.includes(option.type)),
  }));
  const activeGroup = groups.find(group => group.options.some(option => option.type === value)) ?? groups[0];

  return (
    <div className="flex flex-col gap-2">
      <Tabs
        value={activeGroup.value}
        onValueChange={groupValue => {
          const group = groups.find(candidate => candidate.value === groupValue);
          if (group && group !== activeGroup) onChange(group.options[0].type);
        }}
      >
        <Tabs.List className="w-full" aria-label="What the widget shows">
          {groups.map(group => (
            <Tabs.Trigger key={group.value} value={group.value} disabled={disabled}>
              {group.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs>
      <RadioGroup
        value={value}
        disabled={disabled}
        aria-label={activeGroup.label}
        onValueChange={type => onChange(type as PlayerCardReviewDisplayType)}
      >
        {activeGroup.options.map(option => {
          const id = `${idPrefix}-${option.type}`;
          return (
            <Field.Label key={option.type} htmlFor={id} className={cn(value !== option.type && "border-slate-300")}>
              <Field orientation="horizontal">
                <Field.Content>
                  <Field.Title id={`${id}-title`}>{option.title}</Field.Title>
                  <Field.Description id={`${id}-description`}>{option.description}</Field.Description>
                </Field.Content>
                <RadioGroup.Item
                  value={option.type}
                  id={id}
                  aria-labelledby={`${id}-title`}
                  aria-describedby={`${id}-description`}
                />
              </Field>
            </Field.Label>
          );
        })}
      </RadioGroup>
    </div>
  );
};
export default ReviewDisplayTypePicker;
